import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useDrop } from "react-dnd";
import { ReactSortable } from "react-sortablejs";
import { Box, IconButton, Tooltip } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import DragIndicatorIcon from "@mui/icons-material/DragIndicator";
import SportsSoccerIcon from "@mui/icons-material/SportsSoccer";
import { useIntl } from "react-intl";
import RevaText from "components/common/RevaText";
import ItemTypes from "components/common/ItemTypes";
import { CARD_SIZE_STYLES, cardLayoutWidthSx, normalizeCardLayoutSize } from "./branding-defaults";
import {
  getEnabledPreviewCards,
  prioritiesFromOrder,
  resizeCardSizeByDelta,
} from "./branding-card-order";
import { cardImageUrl, resolveBrandingImageUrl } from "./branding-helpers";

const shieldStyle = {
  width: 28,
  height: 28,
  borderRadius: "50%",
  bgcolor: "rgba(255,255,255,0.15)",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
};

const PreviewCardBlock = ({
  card,
  colors,
  label,
  bgImage,
  selected,
  onSelect,
  onRemove,
  onResize,
  sortable = false,
  layoutSize = "MEDIUM",
}) => {
  const intl = useIntl();
  const normalizedLayoutSize = normalizeCardLayoutSize(layoutSize);
  const sizeStyle = CARD_SIZE_STYLES[normalizedLayoutSize] || CARD_SIZE_STYLES.MEDIUM;
  const resizeRef = useRef({ startY: 0, startSize: normalizedLayoutSize });

  const handleResizePointerDown = (event) => {
    if (card.hideSize) return;
    event.preventDefault();
    event.stopPropagation();
    resizeRef.current = { startY: event.clientY, startSize: normalizedLayoutSize };

    const onMove = (moveEvent) => {
      const delta = moveEvent.clientY - resizeRef.current.startY;
      const nextSize = resizeCardSizeByDelta(resizeRef.current.startSize, card.availableSizes, delta);
      if (nextSize !== normalizedLayoutSize) {
        onResize(card, nextSize);
      }
    };

    const onUp = (upEvent) => {
      const delta = upEvent.clientY - resizeRef.current.startY;
      const nextSize = resizeCardSizeByDelta(resizeRef.current.startSize, card.availableSizes, delta);
      onResize(card, nextSize);
      window.removeEventListener("pointermove", onMove);
      window.removeEventListener("pointerup", onUp);
    };

    window.addEventListener("pointermove", onMove);
    window.addEventListener("pointerup", onUp);
  };

  return (
    <Box
      onClick={() => onSelect(card.uploadField)}
      sx={{
        position: "relative",
        mb: 0,
        borderRadius: 1,
        outline: selected ? "2px solid" : "1px dashed transparent",
        outlineColor: selected ? "primary.main" : "transparent",
        cursor: "pointer",
        boxSizing: "border-box",
        height: "100%",
        "&:hover .branding-card-actions": { opacity: 1 },
      }}
    >
      <Box
        className={sortable ? "branding-card-drag-handle" : undefined}
        sx={{
          backgroundColor: colors.backGroundTertiaryColor,
          backgroundImage: bgImage ? `url(${bgImage})` : "none",
          backgroundSize: "cover",
          backgroundPosition: "center",
          color: colors.textPrimaryColor,
          borderRadius: 1,
          minHeight: card.featured ? card.previewHeight : sizeStyle.minHeight,
          padding: card.featured ? "28px 16px" : sizeStyle.padding,
          fontSize: sizeStyle.fontSize,
          fontWeight: 600,
          textAlign: "center",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          cursor: sortable ? "grab" : "pointer",
        }}
      >
        {sortable && (
          <DragIndicatorIcon
            sx={{ position: "absolute", left: 4, top: 4, fontSize: 16, opacity: 0.7, color: colors.textPrimaryColor }}
          />
        )}
        {label}
      </Box>

      <Box
        className="branding-card-actions"
        sx={{
          position: "absolute",
          top: 2,
          right: 2,
          opacity: selected ? 1 : 0,
          transition: "opacity 0.15s",
          display: "flex",
          gap: 0.25,
        }}
      >
        <Tooltip title={intl.formatMessage({ id: "gestion.branding.cards.removeFromPreview" })}>
          <IconButton
            size="small"
            onClick={(event) => {
              event.stopPropagation();
              onRemove(card);
            }}
            sx={{ bgcolor: "rgba(0,0,0,0.45)", color: "white", width: 22, height: 22 }}
          >
            <CloseIcon sx={{ fontSize: 14 }} />
          </IconButton>
        </Tooltip>
      </Box>

      {!card.hideSize && (
        <Box
          onPointerDown={handleResizePointerDown}
          onClick={(event) => event.stopPropagation()}
          sx={{
            position: "absolute",
            left: "50%",
            bottom: -6,
            transform: "translateX(-50%)",
            width: 36,
            height: 8,
            borderRadius: 4,
            bgcolor: selected ? "primary.main" : "grey.500",
            cursor: "ns-resize",
            opacity: selected ? 1 : 0.6,
            "&:hover": { opacity: 1 },
          }}
          title={intl.formatMessage({ id: "gestion.branding.cards.resizeHint" })}
        />
      )}
    </Box>
  );
};

const BrandingInteractivePreview = ({
  form,
  localPreviews = {},
  logoPreview = "",
  selectedUploadField,
  onSelect,
  onPatch,
  onDropCard,
  onReorder,
}) => {
  const intl = useIntl();
  const [footerActive, setFooterActive] = useState(0);

  const colors = useMemo(
    () => ({
      backGroundPrimaryColor: form.backGroundPrimaryColor || "#262626",
      backGroundSecondaryColor: form.backGroundSecondaryColor || "#262626",
      backGroundTertiaryColor: form.backGroundTertiaryColor || "#707070",
      textPrimaryColor: form.textPrimaryColor || "#03F4CF",
      textSecondaryColor: form.textSecondaryColor || "#B9B9B9",
      textTertiaryColor: form.textTertiaryColor || "#03F4CF",
      textQuaternaryColor: form.textQuaternaryColor || "#EDEDED",
    }),
    [form],
  );

  const logoUrl = logoPreview || resolveBrandingImageUrl(form.image);

  const enabledCards = useMemo(() => getEnabledPreviewCards(form), [form]);
  const featuredCard = enabledCards.find((card) => card.featured);
  const sortableCards = useMemo(
    () => enabledCards.filter((card) => !card.featured).map((card) => ({ ...card, id: card.uploadField })),
    [enabledCards],
  );
  const sortableOrderKey = sortableCards.map((card) => card.uploadField).join("|");
  const [localSortable, setLocalSortable] = useState(sortableCards);

  useEffect(() => {
    setLocalSortable(sortableCards);
  }, [sortableOrderKey, sortableCards]);

  const [{ isOver, canDrop }, drop] = useDrop(() => ({
    accept: ItemTypes.BRANDING_CARD,
    drop: (item) => {
      if (item?.uploadField) onDropCard(item.uploadField);
    },
    collect: (monitor) => ({
      isOver: monitor.isOver(),
      canDrop: monitor.canDrop(),
    }),
  }));

  const handleSortList = useCallback(
    (newList) => {
      setLocalSortable(newList);
      onReorder(prioritiesFromOrder(newList.map((item) => item.uploadField)));
    },
    [onReorder],
  );

  const cardLabel = (card) =>
    (card.textKey && form[card.textKey]) || intl.formatMessage({ id: card.defaultTextId });

  return (
    <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
      <RevaText variant="subtitle1" sx={{ mb: 2, textAlign: "center" }}>
        {intl.formatMessage({ id: "gestion.branding.preview.title" })}
      </RevaText>

      <Box
        sx={{
          width: 300,
          borderRadius: 3,
          border: "8px solid",
          borderColor: "common.black",
          bgcolor: colors.backGroundPrimaryColor,
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
          boxShadow: isOver && canDrop ? 6 : 2,
        }}
      >
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            px: 1.25,
            pt: 0.75,
            pb: 1,
            bgcolor: colors.backGroundSecondaryColor,
          }}
        >
          {form.loginLogoEnabled && logoUrl ? (
            <Box
              sx={{
                width: 30,
                height: 30,
                backgroundImage: `url(${logoUrl})`,
                backgroundSize: "cover",
                backgroundPosition: "center",
              }}
            />
          ) : (
            <Box sx={{ width: 30 }} />
          )}
          <Box sx={{ width: 30, height: 30, borderRadius: 1.25, bgcolor: colors.textQuaternaryColor }} />
        </Box>

        <Box
          ref={drop}
          sx={{
            flex: 1,
            minHeight: 420,
            px: 1.5,
            py: 1.5,
            overflowY: "auto",
            bgcolor: isOver && canDrop ? "rgba(76, 175, 80, 0.12)" : "transparent",
            transition: "background-color 0.15s",
            border: isOver && canDrop ? "2px dashed" : "2px dashed transparent",
            borderColor: isOver && canDrop ? "success.main" : "transparent",
            mx: 0.5,
            borderRadius: 1,
          }}
        >
          {featuredCard && (
            <Box sx={{ mb: 1.5, position: "relative" }}>
              <Box
                onClick={() => onSelect(featuredCard.uploadField)}
                sx={{
                  borderRadius: 1,
                  backgroundImage: `url(${cardImageUrl(form, featuredCard, localPreviews)})`,
                  backgroundSize: "cover",
                  backgroundPosition: "center",
                  backgroundColor: colors.backGroundTertiaryColor,
                  px: 2,
                  py: 2.5,
                  outline: selectedUploadField === featuredCard.uploadField ? "2px solid" : "none",
                  outlineColor: "primary.main",
                  cursor: "pointer",
                }}
              >
                <Box sx={{ textAlign: "center", fontSize: 13, fontWeight: 700, color: colors.textPrimaryColor, mb: 1 }}>
                  {cardLabel(featuredCard)}
                </Box>
                <Box sx={{ display: "flex", justifyContent: "space-evenly", mb: 1 }}>
                  <Box sx={{ fontSize: 11, color: colors.textQuaternaryColor }}>
                    {intl.formatMessage({ id: "gestion.branding.preview.date" })}
                  </Box>
                  <Box sx={{ fontSize: 11, color: colors.textQuaternaryColor }}>
                    {intl.formatMessage({ id: "gestion.branding.preview.field" })}
                  </Box>
                </Box>
                <Box sx={{ display: "flex", justifyContent: "space-evenly", alignItems: "center" }}>
                  <Box sx={shieldStyle}>
                    <SportsSoccerIcon sx={{ fontSize: 16, color: colors.textQuaternaryColor }} />
                  </Box>
                  <Box sx={{ color: colors.textTertiaryColor, fontWeight: 700 }}>0</Box>
                  <Box sx={{ color: colors.textQuaternaryColor }}>-</Box>
                  <Box sx={{ color: colors.textTertiaryColor, fontWeight: 700 }}>0</Box>
                  <Box sx={shieldStyle}>
                    <SportsSoccerIcon sx={{ fontSize: 16, color: colors.textQuaternaryColor }} />
                  </Box>
                </Box>
              </Box>
              <Tooltip title={intl.formatMessage({ id: "gestion.branding.cards.removeFromPreview" })}>
                <IconButton
                  size="small"
                  onClick={() => onPatch({ [featuredCard.enabledKey]: false })}
                  sx={{
                    position: "absolute",
                    top: 4,
                    right: 4,
                    bgcolor: "rgba(0,0,0,0.45)",
                    color: "white",
                    width: 22,
                    height: 22,
                  }}
                >
                  <CloseIcon sx={{ fontSize: 14 }} />
                </IconButton>
              </Tooltip>
            </Box>
          )}

          {localSortable.length > 0 ? (
            <ReactSortable
              list={localSortable}
              setList={handleSortList}
              handle=".branding-card-drag-handle"
              animation={150}
              tag="div"
              style={{
                display: "flex",
                flexWrap: "wrap",
                gap: "8px",
                alignContent: "flex-start",
              }}
            >
              {localSortable.map((card) => {
                const layoutSize = normalizeCardLayoutSize(form[card.sizeKey]);
                return (
                  <div
                    key={card.uploadField}
                    style={{
                      boxSizing: "border-box",
                      ...cardLayoutWidthSx(layoutSize),
                    }}
                  >
                    <PreviewCardBlock
                      card={card}
                      colors={colors}
                      label={cardLabel(card)}
                      layoutSize={layoutSize}
                      bgImage={cardImageUrl(form, card, localPreviews)}
                      selected={selectedUploadField === card.uploadField}
                      onSelect={onSelect}
                      onRemove={(c) => onPatch({ [c.enabledKey]: false })}
                      onResize={(c, nextSize) => onPatch({ [c.sizeKey]: nextSize })}
                      sortable
                    />
                  </div>
                );
              })}
            </ReactSortable>
          ) : (
            !featuredCard && (
              <Box sx={{ color: colors.textSecondaryColor, fontSize: 12, textAlign: "center", py: 6 }}>
                {intl.formatMessage({ id: "gestion.branding.preview.dropHint" })}
              </Box>
            )
          )}
        </Box>

        <Box
          sx={{
            display: "flex",
            justifyContent: "space-evenly",
            px: 1.25,
            py: 1,
            bgcolor: colors.backGroundSecondaryColor,
          }}
        >
          {[0, 1].map((index) => (
            <Box
              key={index}
              onClick={() => setFooterActive(index)}
              sx={{
                width: 30,
                height: 30,
                borderRadius: 1.25,
                cursor: "pointer",
                bgcolor: footerActive === index ? colors.textSecondaryColor : colors.textQuaternaryColor,
              }}
            />
          ))}
        </Box>
      </Box>
    </Box>
  );
};

export default BrandingInteractivePreview;
